import React, { useState, useEffect, useMemo, useRef } from "react";

import { useStateManagerContext } from "~/context/stateManager";
import { useTranslation } from "react-i18next";

import { Icon } from "@local-logic/core/icons";
import { useNeighborhoodMap } from "~/sdks/NeighborhoodMap/context";

import { radiusOptions } from "~/sdks/NeighborhoodMap/constants";
import type { NeighborhoodMapProps } from "~/sdks/NeighborhoodMap/types";

import * as styles from "./styles";

export const RadiusDropdown = () => {
  const { t } = useTranslation(["neighborhoodMap", "common"]);
  const ref = useRef<HTMLDivElement>(null);
  const { sdkState } = useStateManagerContext<NeighborhoodMapProps>();
  const { radius, onRadiusChange } = useNeighborhoodMap();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  const options = useMemo(
    () => radiusOptions[sdkState.options?.distanceUnit || "metric"],
    [sdkState.options?.distanceUnit],
  );

  const distanceUnit = useMemo(
    () =>
      t(
        `common:distance.${sdkState.options?.distanceUnit === "metric" ? "kilometer" : "mile"}`,
      ),
    [sdkState.options?.distanceUnit],
  );

  const handleOnOpenChange = (newIsOpen: boolean) => {
    setIsOpen(newIsOpen);
  };

  const handleOnSelect = (option: number) => () => {
    onRadiusChange(option);
    setIsOpen(false);
  };

  return (
    <div ref={ref}>
      <button
        className={styles.trigger}
        onClick={() => handleOnOpenChange(!isOpen)}
      >
        {t("filters.radius.title")}&nbsp;
        <span className={styles.highlight}>
          {t("filters.radius.value", { value: radius, unit: distanceUnit })}
        </span>
        <Icon
          icon={isOpen ? "AngleArrowSmallDown" : "AngleArrowSmallUp"}
          className={styles.icon}
        />
      </button>

      {isOpen && (
        <div className={styles.content}>
          {options.map((radiusOption) => (
            <div
              key={`radius-option-${radiusOption}`}
              onClick={handleOnSelect(radiusOption)}
              className={styles.option}
            >
              {`${radiusOption} ${distanceUnit}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
